import { BackIcon } from "@/components/icons/back-icon";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { onboardingFadeTransition } from "@/utils/transitions/onboarding-fade-transition";
import { useIonRouter } from "@ionic/react";
import { ReactNode } from "react";

const PHASE_TOTAL_STEPS = 22;

interface OnboardingStepShellProps {
  icon: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Overrides the back-button target (defaults to the step's configured previous path). */
  previousPath?: string;
}

export const OnboardingStepShell = ({
  icon,
  title,
  subtitle,
  children,
  previousPath: previousPathOverride,
}: OnboardingStepShellProps) => {
  const navigation = useIonRouter();
  const { info } = useStepRouteInfo();
  const step = info?.step ?? 1;
  const progressPct = Math.min((step / PHASE_TOTAL_STEPS) * 100, 100);
  const previousPath = previousPathOverride ?? info?.previousPath;

  return (
    <div className="relative min-h-full bg-white">
      <div className="to-splash-screen-background/30 pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent" />

      <div className="relative z-10 flex min-h-full flex-col">
        <div className="safe-area-top sticky top-0 z-20 flex items-center gap-8 bg-white pt-5 pr-9 pb-4 pl-5">
          {previousPath ? (
            <button
              type="button"
              aria-label="Go back"
              onClick={() =>
                navigation.push(previousPath, "back", "pop", undefined, onboardingFadeTransition)
              }
              className="flex size-11 shrink-0 items-center justify-center"
            >
              <BackIcon />
            </button>
          ) : (
            <div className="size-11 shrink-0" aria-hidden />
          )}

          <div className="flex h-1 flex-1 overflow-hidden rounded-full bg-[#ebf1ff]">
            <div
              className="bg-purple-700 transition-[width] duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className="flex flex-col items-center px-9 pt-5">
          <div className="flex size-[100px] items-center justify-center rounded-full bg-[#f0e4ff]">
            <img src={icon} alt="" className="size-7 object-contain" />
          </div>
          <h2 className={cn(textVariants.title1, "pt-9 text-center text-black")}>{title}</h2>
          {subtitle && (
            <p className={cn(textVariants.body, "pt-3 text-center text-black")}>{subtitle}</p>
          )}
        </div>

        <div className="flex flex-1 flex-col items-center px-9 pt-9 pb-32">{children}</div>
      </div>
    </div>
  );
};
